import { EarningsItem, EarningsItemWithEntity } from '@api/api/earnings';
import { mapEntity } from '@api/mappers/mapEntity';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { getReportingPeriodLabel } from '@terminal/widgets/earnings/components/reporting-dates/utils';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useState } from 'react';

import type { ThemeEarningsInsightsForEarnings } from './useThemeEarningsInsights';

export const useThemeEarningsDrawer = (
  earningsInsightByEarningsId?: Map<string, ThemeEarningsInsightsForEarnings>
) => {
  const [selectedRow, setSelectedRow] = useState<EarningsItem | undefined>();
  const { deleteQueryParam, setQueryParams } = useQueryParams();
  const { openDrawer } = useEarningsInsightDrawer();
  const { trackThemeEarningsRowClicked } = usePosthogEvents();
  const { data: entityData } = useEntity(selectedRow?.entity_tag);

  const drawerData: EarningsItemWithEntity | undefined = selectedRow && {
    ...selectedRow,
    entity: entityData?.data ? mapEntity(entityData.data) : undefined,
  };

  const onRowClick = (row: EarningsItem) => {
    trackThemeEarningsRowClicked(
      POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
      [row.entity_tag],
      getReportingPeriodLabel(row)
    );

    const insights = earningsInsightByEarningsId?.get(row.id);
    const earningsInsight = insights?.recap ?? insights?.preview;

    if (earningsInsight) {
      openDrawer({
        entityTag: row.entity_tag,
        earningsId: row.id,
        subtab:
          earningsInsight.type === 'preview' || !row.reported
            ? SubTabsType.Preview
            : SubTabsType.Recap,
        source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
      });
      return;
    }

    setSelectedRow(row);
    setQueryParams(
      {
        [queryKeys.activeDrawer]: queryValues.earningsDrawer,
      },
      { replace: true }
    );
  };

  const onDrawerClose = () => {
    deleteQueryParam([queryKeys.activeDrawer], { replace: true });
    setSelectedRow(undefined);
  };

  return { drawerData, onRowClick, onDrawerClose };
};
